import { z } from 'zod';

/** Transport + modality pairs a scenario can be assigned to. */
export const scenarioChannelSchema = z.object({
  transport: z.enum(['PSTN', 'WEBRTC', 'WHATSAPP', 'WEB_WIDGET', 'EMAIL']),
  modality: z.enum(['VOICE', 'TEXT']),
});
export type ScenarioChannel = z.infer<typeof scenarioChannelSchema>;

/** The pairs that exist on the platform; anything else is rejected. */
export const SCENARIO_CHANNELS: ReadonlyArray<ScenarioChannel> = [
  { transport: 'PSTN', modality: 'VOICE' },
  { transport: 'WEBRTC', modality: 'VOICE' },
  { transport: 'WHATSAPP', modality: 'TEXT' },
  { transport: 'WHATSAPP', modality: 'VOICE' },
  { transport: 'WEB_WIDGET', modality: 'TEXT' },
  { transport: 'WEB_WIDGET', modality: 'VOICE' },
  { transport: 'EMAIL', modality: 'TEXT' },
];

export const isValidScenarioChannel = (channel: ScenarioChannel): boolean =>
  SCENARIO_CHANNELS.some(
    (c) => c.transport === channel.transport && c.modality === channel.modality,
  );

/** What the simulated customer is told to be. Same shape as the replay brief. */
export const scenarioBriefSchema = z.object({
  persona: z.string().min(1),
  goal: z.string().min(1),
  facts: z.array(z.string()).default([]),
  objections: z.array(z.string()).default([]),
  openingLine: z.string().min(1),
});
export type ScenarioBrief = z.infer<typeof scenarioBriefSchema>;

/**
 * A plain-English statement judged by the scenario judge.
 * `required` (default): the scenario must exercise it; if the conversation never
 * reached it, that is reported as a coverage gap, never a pass.
 * `conditional`: an optional branch; when it legitimately did not occur the
 * check is neutral (`not_applicable`).
 */
export const scenarioCheckSchema = z.object({
  id: z.string().min(1).max(40),
  text: z.string().min(1).max(500),
  applicability: z.enum(['required', 'conditional']).optional(),
});
export type ScenarioCheck = z.infer<typeof scenarioCheckSchema>;

/** Deterministic rules evaluated in code, never by a model. */
export const scenarioRulesSchema = z.object({
  toolsMustCall: z.array(z.string().min(1)).default([]),
  toolsMustNotCall: z.array(z.string().min(1)).default([]),
  mustSay: z.array(z.string().min(1)).default([]),
  mustNotSay: z.array(z.string().min(1)).default([]),
  outcome: z.string().min(1).nullable().default(null),
  maxTurns: z.number().int().min(1).max(60).nullable().default(null),
});
export type ScenarioRules = z.infer<typeof scenarioRulesSchema>;

const rulesHaveContent = (rules: ScenarioRules): boolean =>
  rules.toolsMustCall.length > 0 ||
  rules.toolsMustNotCall.length > 0 ||
  rules.mustSay.length > 0 ||
  rules.mustNotSay.length > 0 ||
  rules.outcome !== null ||
  rules.maxTurns !== null;

export const scenarioExpectationsSchema = z
  .object({
    checks: z.array(scenarioCheckSchema).default([]),
    rules: scenarioRulesSchema.default({
      toolsMustCall: [],
      toolsMustNotCall: [],
      mustSay: [],
      mustNotSay: [],
      outcome: null,
      maxTurns: null,
    }),
  })
  .refine((e) => e.checks.length > 0 || rulesHaveContent(e.rules), {
    message: 'Add at least one check or rule',
  });
export type ScenarioExpectations = z.infer<typeof scenarioExpectationsSchema>;

export const createScenarioSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(4000).default(''),
  tags: z.array(z.string().min(1).max(50)).default([]),
  brief: scenarioBriefSchema,
  expectations: scenarioExpectationsSchema,
  turnCap: z.number().int().min(2).max(60).default(30),
});
export type CreateScenarioDto = z.infer<typeof createScenarioSchema>;

export const updateScenarioSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).optional(),
  tags: z.array(z.string().min(1).max(50)).optional(),
  brief: scenarioBriefSchema.optional(),
  expectations: scenarioExpectationsSchema.optional(),
  turnCap: z.number().int().min(2).max(60).optional(),
});
export type UpdateScenarioDto = z.infer<typeof updateScenarioSchema>;

export const draftScenarioSchema = z.object({
  description: z.string().min(10).max(4000),
});
export type DraftScenarioDto = z.infer<typeof draftScenarioSchema>;

export const createScenarioAssignmentSchema = z.object({
  scenarioId: z.string().uuid(),
  configurationId: z.string().uuid(),
  transport: scenarioChannelSchema.shape.transport,
  modality: scenarioChannelSchema.shape.modality,
  required: z.boolean().default(false),
});
export type CreateScenarioAssignmentDto = z.infer<typeof createScenarioAssignmentSchema>;

export const updateScenarioAssignmentSchema = z.object({
  required: z.boolean().optional(),
  enabled: z.boolean().optional(),
});
export type UpdateScenarioAssignmentDto = z.infer<typeof updateScenarioAssignmentSchema>;

export const scenarioRunTargetSchema = z.object({
  kind: z.enum(['live', 'branch', 'commit']),
  branchId: z.string().uuid().optional(),
  commitId: z.string().uuid().optional(),
});
export type ScenarioRunTarget = z.infer<typeof scenarioRunTargetSchema>;

export const createScenarioRunSchema = z.object({
  configurationId: z.string().uuid(),
  target: scenarioRunTargetSchema.default({ kind: 'live' }),
  scenarioIds: z.array(z.string().uuid()).optional(),
  channels: z.array(scenarioChannelSchema).optional(),
});
export type CreateScenarioRunDto = z.infer<typeof createScenarioRunSchema>;

/** Stored in improvement_test_cases.judge_output for kind = 'scenario'. */
export interface ScenarioJudgeOutput {
  scenarioName: string;
  checks: Array<{
    id: string;
    text: string;
    /** `inconclusive` = applicable but the evidence cannot decide it;
     *  `not_applicable` = the situation the check concerns never arose. */
    result: 'pass' | 'fail' | 'inconclusive' | 'not_applicable';
    /** Absent on outputs stored before applicability existed (= required). */
    applicability?: 'required' | 'conditional';
    reason: string;
    evidence: string | null;
  }>;
  rules: Array<{ rule: string; result: 'pass' | 'fail'; detail: string }>;
  endedBy: string;
  verdict: {
    result: 'pass' | 'fail' | 'inconclusive';
    reasons: string[];
    /** Required checks the conversation never exercised, kept apart from
     *  evidence uncertainty. Absent on older outputs. */
    coverageGaps?: string[];
  };
}
